<div class="form-dialog">
  <h1><span>{{form_title}}</span></h1>
  @if (policyLoading) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  }
  @if (!policyLoading) {
    <mat-dialog-content class="lock-policy-details">
      <mat-tab-group [disableRipple]="true" #lock_policy_tabs (selectedTabChange)="onTabChange($event)">
        <mat-tab label="Lock policy details">
          <div class="row auto info">
            Please enter the details for the lock policy. The rule can be edited in a future version.
          </div>
          <div class="row">
            <mat-form-field>
              <mat-label>Name</mat-label>
              <input matInput [(ngModel)]="policy.attributes.name" [disabled]="disabled" style="box-sizing: border-box;" required #nameInput="ngModel">
              @if (nameInput.invalid && nameInput.touched) {
                <mat-error>Name is required</mat-error>
              }
            </mat-form-field>
            <mat-checkbox [(ngModel)]="policy.attributes.enabled" [disabled]="disabled" style="margin-left: 24px;align-self:center;">Enabled</mat-checkbox>
          </div>
          <div class="row auto schedule-row">
            <schedule-picker
              [(schedule)]="policy.attributes.schedule"
              class="full-width">
            </schedule-picker>
          </div>
          <div class="row">
            <mat-form-field>
              <mat-label>Attributes to Lock</mat-label>
              <mat-select multiple [(ngModel)]="selectedAttributes" (ngModelChange)="onAttributesToLockChange($event)" [disabled]="disabled">
                @for (group of attributeGroups; track group) {
                  <mat-optgroup [label]="group.label">
                    @for (attr of group.attributes; track attr) {
                      <mat-option [value]="attr">
                        {{ attr | removeUnderscore | titlecase }}
                      </mat-option>
                    }
                  </mat-optgroup>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <!-- Real-time Affected Models Display -->
          <div class="row auto">
            <mat-card class="affected-models-card"
              [class.loading]="validationLoading"
              [class.error]="validationError"
              [class.has-contradiction]="!validationLoading && !validationError && validationResult?.contradiction">
              <mat-card-header>
                <mat-card-title>
                  <mat-icon>info</mat-icon>
                  Policy Impact Preview
                </mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <!-- Loading overlay -->
                @if (validationLoading) {
                  <div class="loading-overlay">
                    <mat-spinner diameter="20"></mat-spinner>
                    <span>Calculating affected models...</span>
                  </div>
                }
                <!-- Error state -->
                @if (validationError) {
                  <div class="error-message">
                    <mat-icon color="warn">error</mat-icon>
                    <div class="error-text">
                      <strong>Unable to validate rule</strong>
                      <span>Please check your rule configuration and try again.</span>
                    </div>
                  </div>
                }
                <!-- Always show stats container (hidden when error) -->
                <div class="stats-container" [class.hidden]="validationError">
                  <!-- No rule message -->
                  @if (!policy.attributes.rule) {
                    <div class="no-rule-message">
                      <mat-icon>rule</mat-icon>
                      <span>Configure a rule to see affected models</span>
                    </div>
                  }
                  <!-- Stats grid -->
                  <div class="stat-row" [class.grayed-out]="validationLoading || !policy.attributes.rule">
                    <div class="stat-item">
                      <span class="stat-value">{{ validationResult?.affected?.component_count ?? 0 }}</span>
                      <span class="stat-label">Components</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ validationResult?.affected?.event_count ?? 0 }}</span>
                      <span class="stat-label">Events</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ validationResult?.affected?.cp_count ?? 0  }}</span>
                      <span class="stat-label">Constant Properties</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ validationResult?.affected?.component_type_ids?.length ?? 0 }}</span>
                      <span class="stat-label">Component Types</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">{{ validationResult?.affected?.event_type_ids?.length ?? 0 }}</span>
                      <span class="stat-label">Event Types</span>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
          <!-- Rule Editor Section -->
          <div class="row auto">
            <mat-card class="rule-editor-card" #ruleEditorCard>
              <mat-card-header>
                <mat-card-title>
                  <mat-icon>rule</mat-icon>
                  Rule Editor
                </mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <rule-builder
                  [rule]="policy.attributes.rule"
                  [contradiction]="getContradiction()"
                  [availableCPIds]="availableCPIds"
                  (ruleChange)="onRuleChanged($event)">
                </rule-builder>
              </mat-card-content>
            </mat-card>
          </div>
          <!-- Type Exclusions Section -->
          <div class="row auto">
            <type-exclusions-panel
              [componentExclusions]="componentTypeExclusions"
              [eventExclusions]="eventTypeExclusions"
              [disabled]="disabled"
              [ruleValidation]="validationResult"
              (componentExclusionsChange)="onComponentTypeExclusionsChange($event)"
              (eventExclusionsChange)="onEventTypeExclusionsChange($event)">
            </type-exclusions-panel>
          </div>
        </mat-tab>
        <mat-tab label="Policy Run History">
          @if (!policy?.id) {
            <div>
              <div class="row auto info">
                You must save the policy before you can view the run history.
              </div>
            </div>
          }
          <!-- Show a note that policy runs will be skipped if the last run still has pending jobs -->
          @if (policy?.id) {
            <div class="row auto info">
              Policy runs will be skipped if the last run still has pending jobs.
            </div>
          }
          @if (policy?.id) {
            <div class="run-history-content">
              @if (runsLoading) {
                <mat-progress-bar mode="indeterminate"></mat-progress-bar>
              }
              @if (runsError) {
                <div class="error-message">
                  {{ runsError }}
                  <button mat-button (click)="loadPolicyRuns()">Retry</button>
                </div>
              }
              @if (!runsLoading && !runsError && runs.length === 0) {
                <div class="no-runs">
                  No runs found for this policy.
                </div>
              }
              @if (!runsLoading && !runsError && runs.length > 0) {
                <div class="table-container">
                  <div class="table-wrapper">
                    <table mat-table [dataSource]="runs" matSort [matSortActive]="currentSortActive" [matSortDirection]="currentSortDirection" (matSortChange)="onSortChange($event)" class="runs-table" multiTemplateDataRows>
                      <!-- Expand Column -->
                      <ng-container matColumnDef="expand">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header disabled></th>
                        <td mat-cell *matCellDef="let run">
                          <button mat-icon-button [disabled]="!run.data?.job_ids?.length" (click)="toggleRunExpansion(run); $event.stopPropagation()">
                            <mat-icon>
                              {{ expandedRun?.id === run.id ? 'expand_less' : 'expand_more' }}
                            </mat-icon>
                          </button>
                        </td>
                      </ng-container>
                      <!-- Start Time Column -->
                      <ng-container matColumnDef="start_time">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>Start Time</th>
                        <td mat-cell *matCellDef="let run">{{ run.start_time | date:'medium':'UTC' }} UTC</td>
                      </ng-container>
                      <!-- End Time Column -->
                      <ng-container matColumnDef="end_time">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>End Time</th>
                        <td mat-cell *matCellDef="let run">{{ run.end_time ? (run.end_time | date:'medium':'UTC') : '-' }} UTC</td>
                      </ng-container>
                      <!-- Components Column -->
                      <ng-container matColumnDef="components">
                        <th mat-header-cell *matHeaderCellDef>Components</th>
                        <td mat-cell *matCellDef="let run">{{ run.data?.component_count ?? 0 }}</td>
                      </ng-container>
                      <!-- Events Column -->
                      <ng-container matColumnDef="events">
                        <th mat-header-cell *matHeaderCellDef>Events</th>
                        <td mat-cell *matCellDef="let run">{{ run.data?.event_count ?? 0 }}</td>
                      </ng-container>
                      <!-- CPs Column -->
                      <ng-container matColumnDef="cps">
                        <th mat-header-cell *matHeaderCellDef>CPs</th>
                        <td mat-cell *matCellDef="let run">{{ run.data?.cp_count ?? 0 }}</td>
                      </ng-container>
                      <!-- Jobs Created Column -->
                      <ng-container matColumnDef="jobs_created">
                        <th mat-header-cell *matHeaderCellDef>Jobs Created</th>
                        <td mat-cell *matCellDef="let run">{{ run.data?.jobs_created ?? 0 }}</td>
                      </ng-container>
                      <!-- Component Types Column -->
                      <ng-container matColumnDef="component_types">
                        <th mat-header-cell *matHeaderCellDef>Component Types</th>
                        <td mat-cell *matCellDef="let run">{{ run.data?.component_type_count ?? 0 }}</td>
                      </ng-container>
                      <!-- Event Types Column -->
                      <ng-container matColumnDef="event_types">
                        <th mat-header-cell *matHeaderCellDef>Event Types</th>
                        <td mat-cell *matCellDef="let run">{{ run.data?.event_type_count ?? 0 }}</td>
                      </ng-container>
                      <!-- Expanded Content -->
                      <ng-container matColumnDef="expandedDetail">
                        <td mat-cell *matCellDef="let run" [attr.colspan]="displayedColumns.length">
                          <div [@detailExpand]="expandedRun?.id === run.id ? 'expanded' : 'collapsed'"
                            class="run-details">
                            @if (expandedRun?.id === run.id) {
                              <div>
                                <h4>Jobs Created in This Run</h4>
                                @if (loadingJobs[run.id]) {
                                  <div class="loading">
                                    <mat-spinner diameter="20"></mat-spinner>
                                    Loading jobs...
                                  </div>
                                }
                                @if (!loadingJobs[run.id] && (!runJobs[run.id] || runJobs[run.id]?.length === 0)) {
                                  <div
                                    class="no-jobs">
                                    No jobs found for this run.
                                  </div>
                                }
                                @if (!loadingJobs[run.id] && runJobs[run.id]?.length > 0) {
                                  <table
                                    class="jobs-table">
                                    <thead>
                                      <tr>
                                        <th>Job ID</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      @for (job of runJobs[run.id]; track job) {
                                        <tr>
                                          <td>#{{ job.id }}</td>
                                          <td>{{ job.status || 'Unknown' }}</td>
                                          <td>{{ job.created_on | date:'short':'UTC' }} UTC</td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                }
                              </div>
                            }
                          </div>
                        </td>
                      </ng-container>
                      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                      <tr mat-row *matRowDef="let run; columns: displayedColumns;"
                        class="run-row"
                        [class.expanded]="expandedRun?.id === run.id"
                      (click)="toggleRunExpansion(run)"></tr>
                      <tr mat-row *matRowDef="let run; columns: ['expandedDetail']" class="detail-row"></tr>
                    </table>
                  </div>
                  <mat-paginator [length]="totalRuns"
                    [pageIndex]="currentPage"
                    [pageSize]="pageSize"
                    [pageSizeOptions]="[10, 20, 50, 100]"
                    (page)="onPageChange($event)"
                    showFirstLastButtons>
                  </mat-paginator>
                </div>
              }
            </div>
          }
        </mat-tab>
      </mat-tab-group>
    </mat-dialog-content>
  }
  <div class="action-buttons">
    <button (click)="onCloseClick()" class="btn-close">Close</button>
    <button (click)="save()" class="btn-save" [disabled]="!canSave || policyLoading">Save</button>
  </div>
</div>
